import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableCellContactButtons,
  TableCellPhone,
  TableHead,
  TableModifier,
  TableRow,
} from '../../../../components/organisms/Table';
import { Pagination } from '../../../../_lib/components/Pagination';
import ContentMessage from '../../../../_lib/components/ContentMessage/ContentMessage';
import { ProfilePictureModifier, ProfilePictureName } from '../../../../_lib/components/ProfilePictureName';
import { TableCellRole } from '../../../../components/organisms/Table/partials/TableCellRole';
import { MemberType } from '../../../../types/api/MemberType';
import { useSonTranslation } from '../../../../translation/useSonTranslation';
import { PaginationItem } from '../../../../_lib/components/Pagination/Pagination';
import { asView } from '../../../../_lib/ViewFetcher';
import { useBlockContext } from '../../../../components/blocks/BlockContext';

type MembersListType = {
  members: MemberType[];
  pageItems?: PaginationItem[];
};

/**
 * Renders the list of Users/Members
 */
function MembersList({ members, pageItems }: MembersListType) {
  const { t } = useSonTranslation('Members');
  const { uuid } = useBlockContext();

  const nameLabel = t('labels.name');
  const roleLabel = t('labels.role');
  const phoneLabel = t('labels.phone');
  const contactLabel = t('labels.contact');
  const caption = t('caption');

  const renderRow = (member: MemberType) => {
    return (
      <TableRow key={`member-${member.id}`}>
        <TableCell dataHead={nameLabel}>
          <ProfilePictureName {...member} modifier={ProfilePictureModifier.ALIGN_TOP} />
        </TableCell>
        <TableCellRole label={roleLabel} roles={member.roles} />
        <TableCellPhone label={phoneLabel} name={member.phoneLink?.name} url={member.phoneLink?.url} />
        <TableCellContactButtons
          label={contactLabel}
          emailUrl={member.emailLink?.url}
          phoneUrl={member.phoneLink?.url}
          personName={member.profileName}
        />
      </TableRow>
    );
  };

  return members.length > 0
    ? (
        <>
          <Table caption={caption}>
            <TableHead>
              <TableRow>
                <TableCell modifier={TableModifier.MWIDTH}>{nameLabel}</TableCell>
                <TableCell>{roleLabel}</TableCell>
                <TableCell>{phoneLabel}</TableCell>
                <TableCell modifier={TableModifier.XXSWIDTH} screenreaderOnly>
                  {contactLabel}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>{members.map(renderRow)}</TableBody>
          </Table>
          {pageItems != null && <Pagination parentHash={uuid} pageItems={pageItems} />}
        </>
      )
    : (
        <ContentMessage text={t('noMembers')} />
      );
}

export const MembersListView = asView(MembersList);
